/**
 * Cart Redux slice
 * @module features/cart/cartSlice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cartService from './cartService';

const initialState = {
  cart: null,
  items: [],
  isLoading: false,
  error: null,
};

export const fetchCart = createAsyncThunk(
  'cart/fetch',
  async (_, thunkAPI) => {
    try {
      return await cartService.getCart();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/add',
  async ({ productId, variantId, quantity }, thunkAPI) => {
    try {
      return await cartService.addToCart(productId, variantId, quantity);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/update',
  async ({ itemId, quantity }, thunkAPI) => {
    try {
      return await cartService.updateCartItem(itemId, quantity);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/remove',
  async (itemId, thunkAPI) => {
    try {
      return await cartService.removeFromCart(itemId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clear',
  async (_, thunkAPI) => {
    try {
      return await cartService.clearCart();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCartError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const handleCartUpdate = (state, action) => {
      state.isLoading = false;
      state.cart = action.payload.cart;
      state.items = action.payload.cart?.items || [];
    };

    builder
      .addCase(fetchCart.pending, (state) => { state.isLoading = true; })
      .addCase(fetchCart.fulfilled, handleCartUpdate)
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addToCart.pending, (state) => { state.isLoading = true; })
      .addCase(addToCart.fulfilled, handleCartUpdate)
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCartItem.fulfilled, handleCartUpdate)
      .addCase(removeFromCart.fulfilled, handleCartUpdate)
      .addCase(clearCart.fulfilled, (state) => {
        state.cart = null;
        state.items = [];
      });
  },
});

export const { clearCartError } = cartSlice.actions;
export default cartSlice.reducer;

// Developed and Designed by Trophy Developers
